// The live connection outside the workspace (the dashboard's Activity, which asks the machine
// over it): opened while a view holds it, with the session list kept in the store so rows can
// name their session. It lingers a little after the last holder lets go, so scrolling a view
// out and back doesn't reconnect. Once the workspace has loaded, it runs the connection (term/manager.ts).

import { conn } from './connection';
import type { Session } from './protocol';
import { store } from '../store';
import { heardMachineClock } from '../ui/time';

const LINGER_MS = 30_000;

let holders = 0;
let open = false;
let closing: ReturnType<typeof setTimeout> | undefined;
/** The workspace's terminals have the connection (term/manager.ts): their handlers keep the session list too,
 *  so a view here never swaps them for its own (the terminals would stop hearing their output) or stops it. */
let workspace = false;

/** The workspace takes the connection over, for as long as the page lives. */
export function workspaceTakesConnection() {
  workspace = true;
  open = false;
  clearTimeout(closing);
}

const setSessions = (sessions: Session[] | undefined) => store.set({ sessions: sessions ?? [], sessionsLoaded: true });

/** Hold the connection open; call the returned function to let go. */
export function holdConnection(): () => void {
  if (workspace) {
    conn.start();
    return () => {};
  }
  clearTimeout(closing);
  holders++;
  if (!open) {
    open = true;
    conn.setHandlers({
      onSessionFrame() {},
      onEvent: (e) => {
        if (e.kind !== 'sessions') return;
        heardMachineClock(e.now);
        setSessions(e.sessions);
      },
      onConnected: () =>
        void conn
          .control<{ sessions: Session[]; now?: string }>('list')
          .then((r) => {
            heardMachineClock(r.now);
            setSessions(r.sessions);
          })
          .catch(() => {}),
    });
    conn.start();
  }
  let held = true;
  return () => {
    if (!held) return;
    held = false;
    if (--holders > 0) return;
    closing = setTimeout(() => {
      open = false;
      if (!workspace) conn.stop();
    }, LINGER_MS);
  };
}
